/**
 * RestoID — settings provider with a graceful load-failure fallback.
 * ---------------------------------------------------------------------------
 * Drop-in for the upstream SettingsProvider: same behavior (load the
 * sign-in-experience settings + i18n, gate the app on them), but if that
 * request fails — e.g. a transient 5xx from the backend, which upstream leaves
 * as an UNCAUGHT rejection and a blank page forever — we render a branded error
 * card with a Retry instead of `null`. The admin-console preview path is
 * delegated to upstream's usePreview unchanged.
 */
import { type ReactElement, useContext, useEffect, useMemo, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import usePreview from '@/Providers/SettingsProvider/use-preview';
import useTheme, { getThemeBySystemConfiguration } from '@/Providers/SettingsProvider/use-theme';
import initI18n from '@/i18n/init';
import { getSignInExperienceSettings } from '@/utils/sign-in-experience';

// Side effect: installs the RestoID corrections to Logto's phrase bundles. It has
// to be imported from here rather than from a page, because the listener must be
// registered before initI18n() below runs and before any component subscribes.
// eslint-disable-next-line import/no-unassigned-import
import '../i18n/phrases';

import { Button, C, FONT, Icon } from './restoid';

const pickLang = (): 'ru' | 'en' => {
  try {
    // The explicit ?lng choice wins; then i18n's <html lang> (only reliable when it
    // initialized); then the browser. RestoID is RU-first, so default to ru.
    const q = new URLSearchParams(window.location.search).get('lng');
    if (q) {
      return q.toLowerCase().startsWith('en') ? 'en' : 'ru';
    }
    const htmlLang = (document.documentElement.lang || '').toLowerCase();
    if (htmlLang.startsWith('ru')) {
      return 'ru';
    }
    if (htmlLang.startsWith('en')) {
      return 'en';
    }
    if ((navigator.language || '').toLowerCase().startsWith('ru')) {
      return 'ru';
    }
  } catch {
    /* Noop */
  }
  return 'ru';
};

const COPY = {
  ru: {
    title: 'Не удалось загрузить',
    message: 'Не получилось загрузить страницу входа. Проверьте подключение и попробуйте снова.',
    retry: 'Повторить',
  },
  en: {
    title: 'Couldn’t load the page',
    message: 'We couldn’t load the sign-in page. Check your connection and try again.',
    retry: 'Try again',
  },
} as const;

/** Branded fallback shown when the sign-in-experience settings fail to load. The
 *  `logto_page-container` wrapper centres it like a normal screen AND lets the
 *  chrome dismiss the boot loader (it watches for that class). */
const LoadError = () => {
  const t = COPY[pickLang()];
  // Display:flex makes restoid.css's align/justify-center apply (the upstream layout
  // normally supplies it via styles.container, which this standalone card doesn't have).
  return (
    <div className="logto_page-container" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="logto_main-content">
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <span
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                background: C.dangerTileStrong,
                border: `1px solid ${C.dangerTileLineStrong}`,
                display: 'grid',
                placeItems: 'center',
                color: C.danger,
              }}
            >
              <Icon name="alert" size={30} sw={1.7} />
            </span>
          </div>
          <h1 style={{ margin: 0, color: C.text, font: `700 21px/1.3 ${FONT}` }}>{t.title}</h1>
          <p style={{ margin: '10px 0 22px', color: C.sub, font: `400 14.5px/1.6 ${FONT}` }}>
            {t.message}
          </p>
          <Button title={t.retry} onClick={() => window.location.reload()} />
        </div>
      </div>
    </div>
  );
};

/** Non-preview settings load that CATCHES failure (vs upstream's uncaught throw). */
const useSettingsLoad = (): boolean => {
  const { isPreview, setExperienceSettings, setTheme } = useContext(PageContext);
  const [failed, setFailed] = useState(false);

  useTheme();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      /* eslint-disable @typescript-eslint/no-unnecessary-condition -- `cancelled` is
         flipped by the cleanup below, which runs after this closure is created; TS's
         flow analysis can't see that, so it wrongly flags these mount guards. */
      try {
        const [settings] = await Promise.all([getSignInExperienceSettings(), initI18n()]);
        if (cancelled) {
          return;
        }
        // Ensure theme is set before rendering with new settings to avoid flashing.
        if (settings.color.isDarkModeEnabled) {
          setTheme(getThemeBySystemConfiguration());
        }
        setExperienceSettings(settings);
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
      /* eslint-enable @typescript-eslint/no-unnecessary-condition */
    })();
    return () => {
      cancelled = true;
    };
  }, [isPreview, setExperienceSettings, setTheme]);

  return failed;
};

/** Preview path (admin-console live preview) — delegates to upstream, never fails. */
const usePreviewLoad = (): boolean => {
  usePreview();
  return false;
};

const SettingsProvider = ({ children }: { readonly children: ReactElement }) => {
  const { isPreview, experienceSettings } = useContext(PageContext);
  const usePageLoad = useMemo(() => (isPreview ? usePreviewLoad : useSettingsLoad), [isPreview]);
  const failed = usePageLoad();

  if (experienceSettings) {
    return children;
  }

  if (failed) {
    return <LoadError />;
  }

  return null;
};

export default SettingsProvider;
